'use client'

import { useState, useEffect } from 'react'
import { useTranslations } from 'next-intl'
import { isNativePlatform } from '@/lib/utils/platform'
import { WifiOff } from 'lucide-react'

/**
 * Network status banner for native apps.
 * Shows a slim, non-intrusive banner at the top when offline.
 * Pattern from Slack, LinkedIn, WhatsApp.
 */
export function NetworkStatusBanner() {
  const t = useTranslations('common')
  const [isOffline, setIsOffline] = useState(false)
  const [isNative, setIsNative] = useState(false)

  useEffect(() => {
    setIsNative(isNativePlatform())

    const handleOnline = () => setIsOffline(false)
    const handleOffline = () => setIsOffline(true)

    // Use browser events for both web and native
    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    // Check initial state
    if (!navigator.onLine) {
      setIsOffline(true)
    }

    // On native, also use Capacitor Network plugin if available
    if (isNativePlatform()) {
      // Dynamic import with type workaround — @capacitor/network types may not be installed
      import(/* webpackIgnore: true */ '@capacitor/network' as string)
        .then(
          (mod: {
            Network: {
              addListener: (event: string, cb: (status: { connected: boolean }) => void) => void
            }
          }) => {
            mod.Network.addListener('networkStatusChange', (status: { connected: boolean }) => {
              setIsOffline(!status.connected)
            })
          }
        )
        .catch(() => {
          // Plugin not available — browser events are fallback
        })
    }

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  // Only show on native apps — web browsers (including localhost)
  // handle offline state natively, and navigator.onLine can false-positive
  if (!isOffline || !isNative) return null

  return (
    <div className={`fixed top-0 left-0 right-0 z-9999 ${isNative ? 'pt-safe' : ''}`}>
      <div className="bg-red-600 text-white text-xs font-medium py-1.5 px-4 flex items-center justify-center gap-2 shadow-md">
        <WifiOff className="w-3.5 h-3.5" />
        <span>{t('noInternet')}</span>
      </div>
    </div>
  )
}
